// AlertsAndNotifications.styles.ts
import styled from "styled-components";

// ===== INTERFACE DO TEMA =====
interface Theme {
  colors: {
    background: string;
    text: string;
    selection: string;
    modalColor: string;
    border: string;
    primary?: string;
    danger?: string;
    selectionDark?: string;
    textOnPrimary?: string;
    label?: string;
    borderColor?: string;
  };
}

// ===== COMPONENTES PRINCIPAIS =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.colors.selectionDark};
    cursor: grab;
  }

  &::-webkit-scrollbar-thumb:active {
    cursor: grabbing;
  }
  &:hover {
    scrollbar-width: auto;
  }
  scrollbar-color: ${(props) => props.theme.colors.selection};
`;

// ===== COMPONENTES DE BOTÃO =====
export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) var(--sp-24);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: background-color 0.3s, transform 0.2s, opacity 0.2s;
  gap: var(--sp-8);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-12) var(--sp-24);
  background-color: #f5f5f5;
  color: #333;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--fs-md);
  font-weight: 500;
  transition: background-color 0.2s, transform 0.2s;
  gap: var(--sp-8);

  &:hover {
    background-color: #e8e8e8;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// ===== COMPONENTES DE ÍCONES =====
export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

// ===== O QUE A ABA PASSOU A PRECISAR AO PERDER OS DADOS DE MENTIRA =====
//
// A aba mostrava três alertas, três templates e três "alertas recentes" escritos
// à mão no JSX. Tirado o mock, aparecem três estados que antes não existiam:
// "não há nada gravado", "isto é o padrão do sistema, ainda não gravado" e "isto
// mudou de lugar". Cada um deles é um componente aqui — nenhum é enfeite.
//
// Mesmos três tons do resto da Configuração (ver o `Aviso` de MeiosPagamento.ts
// e o `AlertMessage` da casca dos modais): ok, atenção, erro.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-10);
  padding: var(--sp-12) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.5;
  border: 1px solid
    ${(props) =>
      props.$tom === "ok"
        ? "#b7e0bd"
        : props.$tom === "erro"
        ? "#f0b7b2"
        : "#f0dcae"};
  background: ${(props) =>
    props.$tom === "ok"
      ? "#eef8ef"
      : props.$tom === "erro"
      ? "#fdf0ef"
      : "#fdf7e7"};
  color: ${(props) =>
    props.$tom === "ok"
      ? "#1e5c28"
      : props.$tom === "erro"
      ? "#8c2b23"
      : "#7a5a12"};
`;

/** "Não há nada gravado" — com o motivo, nunca só com um desenho triste. */
export const CaixaVazia = styled.div`
  padding: var(--sp-32) var(--sp-24);
  border: 1px dashed #cbd6e0;
  border-radius: 8px;
  background: #f8f9fa;
  text-align: center;
`;

export const CaixaVaziaTitulo = styled.div`
  font-size: var(--fs-base);
  font-weight: 600;
  color: #495057;
  margin-bottom: var(--sp-8);
`;

export const CaixaVaziaTexto = styled.div`
  font-size: var(--fs-md);
  color: #6c757d;
  line-height: 1.6;
  max-width: calc(var(--u) * 620);
  margin: 0 auto;
`;

/**
 * O selo que separa "padrão do sistema" de "gravado".
 *
 * Sem ele, o template semeado deste código é indistinguível de um template que a
 * organização salvou — e o usuário passa a acreditar que já configurou algo que
 * não existe em banco nenhum.
 */
export const SeloPadrao = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-4);
  padding: var(--sp-2) var(--sp-8);
  border-radius: 999px;
  font-size: var(--fs-2xs);
  font-weight: 600;
  background: #fdf7e7;
  color: #7a5a12;
  border: 1px solid #f0dcae;
`;

// Exportação padrão para compatibilidade
export default {
  FieldsContainer,
  Button,
  SecondaryButton,
  MaterialIcons,
  Aviso,
  CaixaVazia,
  CaixaVaziaTitulo,
  CaixaVaziaTexto,
  SeloPadrao,
};
